import type { PropsWithChildren } from 'react'
import { useMemo, useReducer } from 'react'
import type {
  ShortVideo,
  ShortVideoBookmark,
  ShortVideoChannel,
  ShortVideoLike,
  ShortVideoSubscribe,
  ShortVideoTopic,
} from '@services/firebase/short-videos'
import {
  initialShortVideosState,
  ShortVideosStateContext,
  type ShortVideosPaginationState,
  type ShortVideosState,
  type ShortVideosStateActions,
  type ShortVideosStateContextValue,
} from './short-videos-state'

type ShortVideosAction =
  | { type: 'set-bookmarks'; payload: ShortVideoBookmark[] }
  | { type: 'set-bootstrap-loading'; payload: boolean }
  | { type: 'set-channels'; payload: ShortVideoChannel[] }
  | { type: 'set-error-message'; payload: string | null }
  | { type: 'set-likes'; payload: ShortVideoLike[] }
  | { type: 'set-page-loading'; payload: boolean }
  | { type: 'set-pagination'; payload: ShortVideosPaginationState }
  | { type: 'set-search-dialog-open'; payload: boolean }
  | { type: 'set-search-loading'; payload: boolean }
  | { type: 'set-search-query'; payload: string }
  | { type: 'set-search-results'; payload: ShortVideo[] }
  | { type: 'set-selected-video'; payload: ShortVideo | null }
  | { type: 'set-subscribes'; payload: ShortVideoSubscribe[] }
  | { type: 'set-topics'; payload: ShortVideoTopic[] }
  | { type: 'set-videos'; payload: ShortVideo[] }
  | { type: 'toggle-selected-topic'; payload: string }

function shortVideosReducer(
  state: ShortVideosState,
  action: ShortVideosAction,
): ShortVideosState {
  switch (action.type) {
    case 'set-bookmarks':
      return { ...state, bookmarks: action.payload }
    case 'set-bootstrap-loading':
      return { ...state, loading: { ...state.loading, bootstrap: action.payload } }
    case 'set-channels':
      return { ...state, channels: action.payload }
    case 'set-error-message':
      return { ...state, errorMessage: action.payload }
    case 'set-likes':
      return { ...state, likes: action.payload }
    case 'set-page-loading':
      return { ...state, loading: { ...state.loading, page: action.payload } }
    case 'set-pagination':
      return { ...state, pagination: action.payload }
    case 'set-search-dialog-open':
      return { ...state, searchDialogOpen: action.payload }
    case 'set-search-loading':
      return { ...state, loading: { ...state.loading, search: action.payload } }
    case 'set-search-query':
      return { ...state, searchQuery: action.payload }
    case 'set-search-results':
      return {
        ...state,
        searchResolvedQuery: state.searchQuery.trim() || null,
        searchResults: action.payload,
      }
    case 'set-selected-video':
      return { ...state, selectedVideo: action.payload }
    case 'set-subscribes':
      return { ...state, subscribes: action.payload }
    case 'set-topics':
      return { ...state, topics: action.payload }
    case 'set-videos':
      return { ...state, feedLoaded: true, videos: action.payload }
    case 'toggle-selected-topic': {
      const isSelected = state.selectedTopicIds.includes(action.payload)

      return {
        ...state,
        selectedTopicIds: isSelected
          ? state.selectedTopicIds.filter((topicId) => topicId !== action.payload)
          : [...state.selectedTopicIds, action.payload],
      }
    }
    default:
      return state
  }
}

export function ShortVideosStateProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(shortVideosReducer, initialShortVideosState)

  const actions = useMemo<ShortVideosStateActions>(
    () => ({
      setBookmarks: (bookmarks) => {
        dispatch({ type: 'set-bookmarks', payload: bookmarks })
      },
      setBootstrapLoading: (isLoading) => {
        dispatch({ type: 'set-bootstrap-loading', payload: isLoading })
      },
      setChannels: (channels) => {
        dispatch({ type: 'set-channels', payload: channels })
      },
      setErrorMessage: (errorMessage) => {
        dispatch({ type: 'set-error-message', payload: errorMessage })
      },
      setLikes: (likes) => {
        dispatch({ type: 'set-likes', payload: likes })
      },
      setPageLoading: (isLoading) => {
        dispatch({ type: 'set-page-loading', payload: isLoading })
      },
      setPagination: (pagination) => {
        dispatch({ type: 'set-pagination', payload: pagination })
      },
      setSearchDialogOpen: (isOpen) => {
        dispatch({ type: 'set-search-dialog-open', payload: isOpen })
      },
      setSearchLoading: (isLoading) => {
        dispatch({ type: 'set-search-loading', payload: isLoading })
      },
      setSearchQuery: (query) => {
        dispatch({ type: 'set-search-query', payload: query })
      },
      setSearchResults: (results) => {
        dispatch({ type: 'set-search-results', payload: results })
      },
      setSelectedVideo: (video) => {
        dispatch({ type: 'set-selected-video', payload: video })
      },
      setSubscribes: (subscribes) => {
        dispatch({ type: 'set-subscribes', payload: subscribes })
      },
      setTopics: (topics) => {
        dispatch({ type: 'set-topics', payload: topics })
      },
      setVideos: (videos) => {
        dispatch({ type: 'set-videos', payload: videos })
      },
      toggleSelectedTopic: (topicId) => {
        dispatch({ type: 'toggle-selected-topic', payload: topicId })
      },
    }),
    [],
  )

  const value = useMemo<ShortVideosStateContextValue>(
    () => ({
      actions,
      state,
    }),
    [actions, state],
  )

  return (
    <ShortVideosStateContext.Provider value={value}>
      {children}
    </ShortVideosStateContext.Provider>
  )
}
